import axios, { InternalAxiosRequestConfig } from 'axios';
import { getSupabase } from '../lib/supabase';
import { trackApiError } from '../lib/errorTracking';
import { useAuthStore } from '../stores/authStore';

const getBaseUrl = () => {
    const baseUrl = import.meta.env.PROD 
        ? import.meta.env.VITE_API_URL 
        : 'http://localhost:3000/api';  // Add /api prefix

    return baseUrl;
};

const api = axios.create({
    baseURL: getBaseUrl(),
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 600000 // 10 minutes timeout
});

// Add auth headers to request
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    try {
        const supabase = getSupabase();
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.access_token) {
            config.headers.Authorization = `Bearer ${session.access_token}`;
        }
        
        return config;
    } catch (error) {
        console.error('Error in request interceptor:', error);
        return config;
    }
});

// Handle response errors globally
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        // Track API errors
        trackApiError(error);

        // Handle specific error cases
        if (error.response?.status === 401) {
            const errorMessage = error.response.data?.message;
            
            // Only logout if it's specifically an authentication error
            if (errorMessage === 'Missing or invalid authorization header' || 
                errorMessage === 'Invalid or expired access token') {
                // Clear auth store
                useAuthStore.getState().clearAuth();
                
                // Sign out from Supabase
                const supabase = getSupabase();
                await supabase.auth.signOut();
                
                // Redirect to login
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export default api; 